import React, { useEffect, useRef, useState } from "react";

// ASSETS
import LeftArrow from "assets/icons/ArrowLeftHovered.svg";
import CoverPlaceholder from "assets/img/CoverPlaceholder.png";
import { ReactComponent as NoDataDark } from "assets/svg/NoDataDark.svg";

// STYLES
import {
  BackToGuidesButton,
  BackToGuidesImage,
  BackToWrapper,
  ContentSectionWrapper,
  HeaderSectionWrapper,
  InternationalPartnersCardIcon,
  NoDataContainer,
  PartnersCardIcon,
  PartnersCardIconWrapper,
  PartnersCardTextWrapper,
  PartnersCardWrapper,
  PartnersCardsWrapper,
  PartnersContainer,
  SvgContainer,
  TitleSectionWrapper,
  TitleWrapper,
} from "./PartnersStyled";

// LIBRARIES
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// MISC
import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import { COLORS } from "constants/variables";
import { ModalTypes } from "components/Modal/ModalModel";

// REDUX
import { useSelector } from "react-redux";
import { accessibilityBiggerCursorSelector } from "slices/UISlice/uiSliceSelectors";

// COMPONENTS
import Pagination from "components/Atoms/Paginations/Pagination";
import HeaderTab from "components/HeaderTab/HeaderTab";
import CustomText from "components/Atoms/CustomText/CustomText";
import Modal from "components/Modal/Modal";
import aboutService from "services/aboutService";
import { uploadBaseUrl } from "services/base";
import { PartnersResponse } from "./PartnersModel";

// HOOKS
import usePagination from "hooks/usePagination";

const Partners = () => {
  // PROPERTIES
  const ELEMENTS_PER_PAGE = 12;

  // LIBRARY CONSTANTS
  const navigate = useNavigate();

  // STATE CONSTANTS
  const [modalState, setModalState] = useState({
    isVisible: false,
    type: "",
    item: null,
  });
  const biggerCursor = useSelector(accessibilityBiggerCursorSelector);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1); // Declare currentPage state
  const [totalPages, setTotalPages] = useState<number>(1); // Declare totalPages state
  const { t } = useTranslation();
  const [partners, setPartners] = useState<PartnersResponse>();
  const topRef = useRef(null);

  const types = ["national", "international", "organisme-internationale"];

  // Fetch partners when the selected tab or page changes
  useEffect(() => {
    setPartners(undefined);

    aboutService
      .getPartners(types[selectedTab], currentPage, ELEMENTS_PER_PAGE) // Pass pageSize
      .then((response) => {
        // console.log("getPartners response", response);
        setPartners(response);
        setTotalPages(response?.meta?.pagination?.pageCount || 1);
      })
      .catch((err) => {
        console.error("getPartners error", err);
      });

    topRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [selectedTab, currentPage]);

  // Tab change handler
  const handleTabSelect = (selectedTabId: number) => {
    setSelectedTab(selectedTabId);
    setCurrentPage(1); // Reset to page 1 when switching tabs
  };

  // Use pagination hook
  const { currentData, handlePageChange } = usePagination(
    partners?.data || [],
    ELEMENTS_PER_PAGE,
    topRef
  );

  const content = (
    <PartnersCardsWrapper>
      {currentData.length > 0 ? (
        currentData.map((partner) => (
          <PartnersCardWrapper
            key={partner.id}
            onClick={() => handleClick(partner)}
            biggerCursor={biggerCursor}
          >
            {selectedTab === 1 ? (
              <PartnersCardIconWrapper>
                <InternationalPartnersCardIcon
                  src={
                    partner?.attributes?.logo?.data?.attributes?.url?.trim()
                      ? `${uploadBaseUrl}${partner?.attributes?.logo?.data?.attributes?.url}`
                      : CoverPlaceholder
                  }
                />
              </PartnersCardIconWrapper>
            ) : (
              <PartnersCardIconWrapper>
                <PartnersCardIcon
                  src={
                    partner?.attributes?.logo?.data?.attributes?.url?.trim()
                      ? `${uploadBaseUrl}${partner?.attributes?.logo?.data?.attributes?.url}`
                      : CoverPlaceholder
                  }
                />
              </PartnersCardIconWrapper>
            )}
            <PartnersCardTextWrapper>
              <CustomText
                text={partner?.attributes?.title}
                type={TextType.H5bold}
                color={COLORS.RaisinBlack}
              />
            </PartnersCardTextWrapper>
          </PartnersCardWrapper>
        ))
      ) : (
        <NoDataContainer>
          <SvgContainer>
            <NoDataDark />
          </SvgContainer>
          <CustomText
            text={t("app.noData")}
            type={TextType.P1}
            color={COLORS.MetalicSilver}
          />
        </NoDataContainer>
      )}
    </PartnersCardsWrapper>
  );

  const tabs = [
    {
      id: 0,
      tabTitle: t("view.partners.nationalPartnersTab"),
      content: content,
    },
    {
      id: 1,
      tabTitle: t("view.partners.internationalPartnersTab"),
      content: content,
    },
    {
      id: 2,
      tabTitle: t("view.partners.internationalOrgansTab"),
      content: content,
    },
  ];

  const handleClick = (partner) => {
    setModalState({
      isVisible: true,
      type: ModalTypes.PARTNERS,
      item: partner,
    });
  };

  return (
    <PartnersContainer ref={topRef}>
      <TitleSectionWrapper>
        <BackToWrapper onClick={() => navigate(-1)} biggerCursor={biggerCursor}>
          <BackToGuidesImage src={LeftArrow} alt="backIcon" />
          <BackToGuidesButton>
            <CustomText
              text={t("view.about.aboutDSU.button")}
              type={TextType.H5boldspaced}
              color={COLORS.RaisinBlack}
            />
          </BackToGuidesButton>
        </BackToWrapper>
        <TitleWrapper>
          <CustomText type={TextType.H2} text={t("view.partners.title")} />
        </TitleWrapper>
      </TitleSectionWrapper>

      <HeaderSectionWrapper>
        <HeaderTab tabs={tabs} onTabSelect={handleTabSelect} />
      </HeaderSectionWrapper>

      <ContentSectionWrapper>
        {tabs[selectedTab].content}
        {totalPages > 1 && (
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        )}
      </ContentSectionWrapper>
      <Modal
        isVisible={modalState.isVisible}
        type={modalState.type as ModalTypes}
        item={modalState.item}
        onClose={() =>
          setModalState({ isVisible: false, type: ModalTypes.NONE, item: null })
        }
      />
    </PartnersContainer>
  );
};

export default Partners;
